import { memo, useId } from 'react';
import { heroPluginFor } from '@kidshome/hero-content/art';
import type { HeroMood } from '@kidshome/hero-content/art';
import { type EquippedLook } from '@/entities/cosmetics';

/** An empty equipped look — the figure with nothing worn (the default for a plain hero render). */
const NO_LOOK: EquippedLook = {};

/**
 * THE HERO FIGURE — the hero at a given form, wearing everything equipped (ADR-0098/0099/0102). The ONE
 * component that draws "this hero wearing this look": the kid `/hero`, the kid wardrobe, and the platform
 * matrix all render it (the kid + platform surfaces via {@link HeroStage}), so the views are the same art
 * by construction. Lives in `entities/hero` — the single home for all hero render (ADR-0102 consolidation).
 *
 * Hero-AGNOSTIC: it resolves the hero's render plugin ({@link heroPluginFor}) and delegates — it never
 * branches on a hero id. The princess plugin composes her illustrated form with every worn cosmetic; any
 * other archetype resolves to the neutral procedural sprite (no cosmetics until that hero ships a plugin).
 * It owns the render-instance `uid` (a `useId`) so the same look drawn several times on one page never shares
 * an internal SVG gradient/clip id. An optional `mood` lets a surface ask the hero to react (ADR-0102).
 * Decorative (the SVG is `aria-hidden`).
 *
 * Memoized (ADR-0102, Plan D): the composed figure is the heaviest thing the kid renders, and `/hero` polls
 * the narrator every few seconds. With a stable `look` reference (`useEquippedLook` memoizes it) the figure
 * only re-renders when its form/look/mood actually change, not on every poll.
 */
export const HeroFigure = memo(function HeroFigure({
  archetypeId,
  stageIndex,
  stageCount,
  look = NO_LOOK,
  mood,
  className,
}: {
  archetypeId: string | null;
  stageIndex: number;
  stageCount?: number;
  look?: EquippedLook;
  mood?: HeroMood;
  className?: string;
}): React.ReactElement {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  return heroPluginFor(archetypeId).renderFigure({
    stageIndex,
    stageCount,
    look,
    mood,
    className,
    uid,
  });
});
